/**
 * matchDriverForProduct — given a product object and the public driver list,
 * return the best-matching driver (or null).
 *
 * Matching order:
 *  1. Explicit product.driver_id (admin-configured)
 *  2. component_type heuristic from product.category + tags:
 *       - category=measure_first + tag includes 'p1'           → 'p1_meter'
 *       - category=measure_first + tag includes 'tussenstekker'/'smart_plug' → 'smart_plug'
 *       - category=manual_start + tag includes 'smart_plug'    → 'smart_plug'
 *       - category=auto_switching + tag includes 'ev'          → 'ev_charger'
 *       - category=auto_switching + tag includes 'heatpump'    → 'heat_pump'
 *       - category=installer                                   → 'fuse_box_3_phase' / installer-only
 *  3. Brand match against driver.supported_brands (case-insensitive substring
 *     in product.name).
 *
 * Returns null if no driver fits. Callers fall back to "Handmatige route
 * beschikbaar".
 */
const NAME_BRAND_RULES = [
  { needle: 'homewizard', type: 'p1_meter' },
  { needle: 'youless',    type: 'p1_meter' },
  { needle: 'plugwise',   type: 'p1_meter' },
  { needle: 'dsmr',       type: 'p1_meter' },
  { needle: 'shelly',     type: 'smart_plug' },
  { needle: 'tp-link',    type: 'smart_plug' },
  { needle: 'kasa',       type: 'smart_plug' },
];

function guessComponentType(product) {
  if (!product) return null;
  const cat = product.category || '';
  const tags = (product.tags || []).map(t => (t || '').toLowerCase());
  const name = (product.name || '').toLowerCase();

  if (tags.includes('p1') || /p1[\s-]?meter|p1\sdongle/.test(name)) return 'p1_meter';
  if (tags.includes('ev')) return 'ev_charger';
  if (tags.includes('heatpump') || tags.includes('warmtepomp')) return 'heat_pump';
  if (tags.includes('battery') || tags.includes('batterij')) return 'home_battery';
  if (tags.includes('boiler')) return 'boiler';
  if (tags.includes('thermostaat') || tags.includes('thermostat')) return 'smart_thermostat';
  if (tags.includes('tussenstekker') || tags.includes('smart_plug') || tags.includes('plug')) return 'smart_plug';

  if (cat === 'measure_first') {
    if (/p1|slimme.?meter|dongle/.test(name)) return 'p1_meter';
    if (/stekker|plug/.test(name)) return 'smart_plug';
  }
  if (cat === 'manual_start' && /stekker|plug|timer/.test(name)) return 'smart_plug';
  if (cat === 'auto_switching') {
    if (/laadpaal|ev|wallbox|easee|zappi|alfen/.test(name)) return 'ev_charger';
    if (/warmtepomp|heat\s?pump/.test(name)) return 'heat_pump';
    if (/relay|relais|boiler/.test(name)) return 'boiler';
  }
  if (cat === 'installer') return null; // route via inquiry/installer flow
  return null;
}

function brandFromProductName(name) {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const rule of NAME_BRAND_RULES) {
    if (lower.includes(rule.needle)) return rule.needle;
  }
  return null;
}

export function matchDriverForProduct(product, drivers) {
  if (!product || !Array.isArray(drivers) || drivers.length === 0) return null;

  // 1. Explicit driver_id wins (admin-configured)
  if (product.driver_id) {
    const direct = drivers.find(d => d.driver_id === product.driver_id);
    if (direct) return direct;
  }

  const type = product.component_type || guessComponentType(product);
  if (!type) return null;

  const ofType = drivers.filter(d => d.component_type === type);
  if (ofType.length === 0) return null;

  // 2. Brand match by name substring against supported_brands
  const brand = brandFromProductName(product.name);
  if (brand) {
    const byBrand = ofType.find(d =>
      (d.supported_brands || []).some(b => (b || '').toLowerCase().includes(brand))
    );
    if (byBrand) return byBrand;
  }

  // 3. Otherwise the first (typically most generic) driver of that type
  return ofType[0];
}

export default matchDriverForProduct;
